/**
 * FlowDemo — a faithful, interactive replica of the Inferrex platform that runs
 * as ONE end-to-end flow with state that persists across every screen:
 *
 *   Connections → infer a system's schema (some fields land unrecognised)
 *   Field mappings → recognised fields auto-map by concept; unrecognised ones you
 *                    map by hand against the target (or add a new field); two
 *                    systems feeding one field reconcile by lineage
 *   Self-healing → a provider renames a field; the mapping repairs itself
 *   Coverage → your stack, already in the corpus
 *
 * Connecting a system, approving fields, mapping an unrecognised field and
 * choosing a source-of-truth rule all persist as you move between screens via
 * the clickable sidebar rail.
 *
 * Two modes: autoplay (loops, no controls — embeds) and interactive (default).
 * Composes the real @inferrex/ui Card/Badge/Button primitives + tokens — no
 * literal palette, no inline CSS (Rules 18, 33). Honours prefers-reduced-motion.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Cable, Workflow, RefreshCcw, Gauge, Settings, Globe, Layers, Check, ArrowRight,
  ArrowRightLeft, CheckCircle2, AlertTriangle, Play, Shield, ShieldAlert,
  RadioTower, Pencil, Sparkles, Plus, X, Loader2, ScanSearch,
} from 'lucide-react';
import { cn, confidenceColor, confidenceLabel } from '../../utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../card';
import { Badge } from '../badge';
import { Button } from '../button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../select';
import {
  SOURCES, STACK_TOOLS, INFER_SAMPLE, AUTHORITY_RULES, GOLDEN_CUSTOMER, CONCEPT_ORDER,
  type Source, type DemoField, type Protocol,
} from './flow-demo-data';

export type FlowScenario = 'infer' | 'self-heal' | 'golden-record' | 'stack';

export interface FlowDemoProps {
  readonly scenario: FlowScenario;
  readonly autoplay?: boolean | undefined;
}

/* ── badges ─────────────────────────────────────────────────────────────── */

const TYPE_TONE: Record<string, string> = {
  text: 'bg-muted text-muted-foreground',
  object: 'bg-muted text-muted-foreground',
  number: 'bg-chart-1/15 text-chart-1',
  id: 'bg-chart-8/15 text-chart-8',
  email: 'bg-chart-1/15 text-chart-1',
  phone: 'bg-chart-1/15 text-chart-1',
  url: 'bg-chart-1/15 text-chart-1',
  currency: 'bg-chart-2/15 text-chart-2',
  date: 'bg-chart-3/15 text-chart-3',
  datetime: 'bg-chart-3/15 text-chart-3',
  boolean: 'bg-chart-5/15 text-chart-5',
  enum: 'bg-chart-7/15 text-chart-7',
  location: 'bg-chart-6/15 text-chart-6',
  reference: 'bg-chart-4/15 text-chart-4',
  identity: 'bg-chart-4/15 text-chart-4',
};

const PROTOCOL_TONE: Record<Protocol, string> = {
  REST: 'bg-primary/10 text-primary',
  GraphQL: 'bg-chart-5/15 text-chart-5',
  SOAP: 'bg-chart-3/15 text-chart-3',
  gRPC: 'bg-chart-2/15 text-chart-2',
};

const CONF_TONE = {
  high: 'bg-success/15 text-success',
  medium: 'bg-warning/15 text-warning',
  low: 'bg-destructive/15 text-destructive',
} as const;

function TypeBadge({ type }: { type: string }) {
  return <span className={cn('shrink-0 rounded-md px-1.5 py-0.5 font-mono text-2xs', TYPE_TONE[type] ?? TYPE_TONE['text'])}>{type}</span>;
}
function ConfBadge({ value }: { value: number }) {
  return <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-2xs font-medium tabular-nums', CONF_TONE[confidenceColor(value)])}>{confidenceLabel(value)}</span>;
}
function ProtocolBadge({ protocol }: { protocol: Protocol }) {
  return <span className={cn('shrink-0 rounded-full px-2 py-0.5 font-mono text-3xs uppercase tracking-wider', PROTOCOL_TONE[protocol])}>{protocol}</span>;
}

/**
 * Whether a field needs a human nod, and why — derived from REAL figures so the
 * wording can never contradict the percentage. Unrecognised fields get mapped
 * downstream; sensitive PII always needs a nod; otherwise only genuinely low
 * confidence does.
 */
function reviewNote(f: DemoField): { kind: 'unmapped' | 'sensitive' | 'low'; text: string } | null {
  if (f.concept === '') return { kind: 'unmapped', text: 'Unrecognised — map it in Field mappings' };
  if (f.sensitive) return { kind: 'sensitive', text: 'Sensitive data — confirm before you ingest it' };
  if (confidenceColor(f.confidence) === 'low') return { kind: 'low', text: 'Low signal — confirm the classification' };
  return null;
}

/**
 * Lightweight live classifier for the "infer a schema yourself" box — reads a
 * field's name + sample value and returns the inferred type. Deterministic,
 * client-side, no network; a real (if simple) inference, not a canned answer.
 */
interface InferredField { readonly key: string; readonly fieldType: string; readonly classification: string; readonly confidence: number }
function inferField(key: string, value: unknown): InferredField {
  const k = key.toLowerCase();
  const v = typeof value === 'string' ? value : '';
  const isEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);
  const isUrl = /^https?:\/\//i.test(v);
  const isIso = /^\d{4}-\d{2}-\d{2}/.test(v);
  const numeric = typeof value === 'number' || (/^-?\d+(\.\d+)?$/.test(v) && v !== '');
  const out = (fieldType: string, classification: string, confidence: number): InferredField => ({ key, fieldType, classification, confidence });
  if (/e-?mail/.test(k) || isEmail) return out('email', 'person · email', isEmail ? 0.99 : 0.95);
  if (/(phone|tel|mobile|msisdn)/.test(k)) return out('phone', 'person · phone', 0.94);
  if (/currency/.test(k)) return out('currency', 'money · currency', 0.96);
  if (/(amount|price|total|cost|salary|revenue|balance|fee|mrr|arr)/.test(k)) return out('currency', 'money · amount', 0.9);
  if (/(created|updated|_at$|timestamp|signup|date|dob|birth)/.test(k) || isIso) return out('datetime', 'time · date', isIso ? 0.96 : 0.9);
  if (typeof value === 'boolean' || /^(is|has|can|should)_|_(enabled|active)$|^(enabled|active|verified)$/.test(k)) return out('boolean', 'flag · boolean', 0.92);
  if (/(url|link|website|href|domain)/.test(k) || isUrl) return out('url', 'web · url', isUrl ? 0.97 : 0.9);
  if (/country/.test(k)) return out('location', 'location · country', 0.86);
  if (/(city|town|address|postcode|postal|zip|county|state|region)/.test(k)) return out('location', 'location · place', 0.84);
  if (/(^id$|_id$|uuid|guid|_ref$|reference)/.test(k)) return out('id', 'reference · id', 0.9);
  if (/(first_?name|last_?name|full_?name|display_?name|^name$|title|contact)/.test(k)) return out('text', 'person · name', 0.88);
  if (/(status|stage|type$|category|kind|state|tier)/.test(k)) return out('enum', 'category · status', 0.82);
  if (numeric) return out('number', 'number · numeric', 0.85);
  if (value !== null && typeof value === 'object') return out('object', 'extension · object', 0.6);
  if (v.length > 0) return out('text', 'text · freeform', 0.7);
  return out('object', 'unrecognised', 0.45);
}
function inferSchema(json: string): { fields: InferredField[]; error?: string } {
  let parsed: unknown;
  try { parsed = JSON.parse(json); } catch { return { fields: [], error: 'Not valid JSON — paste an object like { "email": "…", "amount": 4999 }.' }; }
  const obj = Array.isArray(parsed) ? parsed[0] : parsed;
  if (!obj || typeof obj !== 'object') return { fields: [], error: 'Paste a JSON object (or an array of objects).' };
  const entries = Object.entries(obj as Record<string, unknown>);
  if (entries.length === 0) return { fields: [], error: 'No fields found.' };
  return { fields: entries.slice(0, 12).map(([k, v]) => inferField(k, v)) };
}

/* ── motion driver ──────────────────────────────────────────────────────── */

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function useReveal(total: number, autoplay: boolean, resetKey: string) {
  const reduced = prefersReducedMotion();
  const settled = !autoplay || reduced;
  const [n, setN] = useState(settled ? total : 0);
  const [playing, setPlaying] = useState(autoplay && !reduced);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setN(settled ? total : 0);
    setPlaying(autoplay && !reduced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, total]);

  useEffect(() => {
    if (!playing) return;
    if (n >= total) {
      if (!autoplay) { setPlaying(false); return; }
      timer.current = setTimeout(() => setN(0), 1700);
      return () => { if (timer.current) clearTimeout(timer.current); };
    }
    timer.current = setTimeout(() => setN((x) => x + 1), 460);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [playing, n, total, autoplay]);

  return { n, playing, play: () => { setN(reduced ? total : 0); setPlaying(true); } };
}

const byKey = (k: string): Source => SOURCES.find((s) => s.key === k) ?? SOURCES[0]!;
const mapKey = (sourceKey: string, apiName: string) => `${sourceKey}::${apiName}`;
const slug = (s: string) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

/* ── shared, persistent store ───────────────────────────────────────────── */

function useDemoStore() {
  const [connected, setConnected] = useState<string[]>(['stripe', 'hubspot', 'legacy']);
  const [viewing, setViewing] = useState('stripe');
  const [approved, setApproved] = useState<Record<string, string[]>>({});
  const [manualMap, setManualMap] = useState<Record<string, string>>({});
  const [authority, setAuthority] = useState<Record<string, string>>({});
  const [accepted, setAccepted] = useState(false);

  return {
    connected, viewing, approved, manualMap, authority, accepted, setViewing, setAccepted,
    isConnected: (k: string) => connected.includes(k),
    toggleConnected: (k: string) =>
      setConnected((c) => (c.includes(k) ? (c.length > 1 ? c.filter((x) => x !== k) : c) : [...c, k])),
    approveAll: (sk: string, names: string[]) => { setApproved((a) => ({ ...a, [sk]: names })); },
    approveField: (sk: string, name: string) => setApproved((a) => ({ ...a, [sk]: [...(a[sk] ?? []), name] })),
    setManual: (sk: string, api: string, val: string) => setManualMap((m) => {
      const key = mapKey(sk, api); const next = { ...m };
      if (!val) delete next[key]; else next[key] = val;
      return next;
    }),
    setAuthorityRule: (concept: string, rule: string) => setAuthority((a) => ({ ...a, [concept]: rule })),
  };
}
type Store = ReturnType<typeof useDemoStore>;

/* ── product chrome ─────────────────────────────────────────────────────── */

type View = FlowScenario;
const VIEW_META: Record<View, { rail: string; url: string; title: string }> = {
  infer: { rail: 'connections', url: '/connections', title: 'Connections' },
  'golden-record': { rail: 'pipelines', url: '/pipelines/mappings', title: 'Field mappings' },
  'self-heal': { rail: 'sync', url: '/sync/health', title: 'Self-healing' },
  stack: { rail: 'intelligence', url: '/intelligence/coverage', title: 'Connect a source' },
};
const RAIL: readonly { key: string; icon: typeof Cable; label: string; view: View | null }[] = [
  { key: 'connections', icon: Cable, label: 'Connections', view: 'infer' },
  { key: 'pipelines', icon: Workflow, label: 'Field mappings', view: 'golden-record' },
  { key: 'sync', icon: RefreshCcw, label: 'Self-healing', view: 'self-heal' },
  { key: 'intelligence', icon: Gauge, label: 'Coverage', view: 'stack' },
  { key: 'settings', icon: Settings, label: 'Settings', view: null },
];

function DemoWindow({ url, title, active, onNavigate, children }: {
  url: string; title: string; active: string; onNavigate?: (v: View) => void; children: React.ReactNode;
}) {
  const interactive = !!onNavigate;
  return (
    <div className="overflow-hidden rounded-card border border-border bg-card shadow-card">
      <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-3 py-2">
        <span className="flex gap-1.5" aria-hidden="true">
          <span className="size-2.5 rounded-full bg-muted-foreground/25" />
          <span className="size-2.5 rounded-full bg-muted-foreground/25" />
          <span className="size-2.5 rounded-full bg-muted-foreground/25" />
        </span>
        <span className="ml-2 inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-0.5 font-mono text-2xs text-muted-foreground">
          <span className="text-success">●</span>app.inferrex.com{url}
        </span>
      </div>
      <div className="flex">
        <nav className="hidden w-12 shrink-0 flex-col items-center gap-1 border-r border-border bg-muted/20 py-3 sm:flex" aria-label="Demo navigation">
          {RAIL.map(({ key, icon: Icon, label, view }) => {
            const isActive = key === active;
            const cls = cn('flex size-8 items-center justify-center rounded-md transition-colors', isActive ? 'bg-primary/12 text-primary' : 'text-muted-foreground/60');
            return interactive && view
              ? <button key={key} type="button" title={label} aria-label={label} aria-current={isActive} onClick={() => onNavigate(view)} className={cn(cls, 'hover:bg-primary/10 hover:text-primary')}><Icon className="size-4.5" /></button>
              : <span key={key} className={cls} aria-hidden="true"><Icon className="size-4.5" /></span>;
          })}
        </nav>
        <div className="min-w-0 flex-1">
          <header className="flex items-center justify-between border-b border-border px-4 py-2.5">
            {/* h2: this title labels the demo region, which sits directly under the
                page hero (h1) with no section heading between — h2 keeps the order
                contiguous and gives the demo's inner panel headings a valid parent.
                Size is set by the class, not the tag. */}
            <h2 className="text-sm font-semibold text-foreground">{title}</h2>
            <span className="size-7 rounded-full bg-muted" aria-hidden="true" />
          </header>
          <div className="space-y-4 bg-muted/10 p-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

function SourceTabs({ activeKey, onPick, connected }: { activeKey: string; onPick: (k: string) => void; connected: string[] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {SOURCES.map((s) => (
        <button key={s.key} type="button" onClick={() => onPick(s.key)}
          className={cn('inline-flex items-center gap-2 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
            s.key === activeKey ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:text-foreground')}>
          {connected.includes(s.key) && <Check className="size-3 text-success" />}
          {s.name}<ProtocolBadge protocol={s.protocol} />
        </button>
      ))}
    </div>
  );
}

/* ── Connections (infer) ────────────────────────────────────────────────── */

function ConnectionBody({ autoplay, store }: { autoplay: boolean; store: Store }) {
  const provider = byKey(store.viewing);
  const { n, playing, play } = useReveal(provider.fields.length, autoplay, provider.key);
  const approvedList = store.approved[provider.key] ?? [];
  const allApproved = autoplay ? n >= provider.fields.length : approvedList.length >= provider.fields.length;
  const reviewCount = provider.fields.filter((f) => reviewNote(f) !== null).length;
  const connected = store.isConnected(provider.key);

  return (
    <>
      {!autoplay && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SourceTabs activeKey={provider.key} onPick={store.setViewing} connected={store.connected} />
          <Button size="sm" variant="outline" onClick={play} className="gap-1.5"><Play className="size-3.5" /> Run inference</Button>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><Globe className="size-5" /></span>
              <div>
                <CardTitle className="text-base">{provider.displayName}</CardTitle>
                <CardDescription className="mt-0.5 font-mono text-xs">{provider.baseUrl}</CardDescription>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {provider.noSpec && <Badge className="inline-flex items-center gap-1 bg-chart-7/15 text-chart-7"><Sparkles className="size-3" /> No spec</Badge>}
              <Badge className={CONF_TONE[confidenceColor(provider.overallConfidence)]}>{confidenceLabel(provider.overallConfidence)} confidence</Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-muted-foreground">
            <span>1 entity · {provider.entity}</span>
            <span>{provider.fields.length} fields</span>
            <span className="inline-flex items-center gap-1"><Shield className="size-3.5" />{provider.authMethod}</span>
            <span>via {provider.inferenceMethod}</span>
          </div>
          {provider.noSpec && (
            <p className="mt-2 text-xs text-muted-foreground">
              No OpenAPI, no docs, cryptic field names — Inferrex probes live responses and infers each field from the <span className="text-foreground">values</span> it sees.
            </p>
          )}
          {!autoplay && (
            <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-border pt-3">
              <Button size="sm" onClick={() => store.approveAll(provider.key, provider.fields.map((f) => f.apiName))} disabled={allApproved} className="gap-1.5">
                <CheckCircle2 className="size-4" />{allApproved ? 'Approved' : 'Approve all'}
              </Button>
              <Button size="sm" variant={connected ? 'outline' : 'default'} onClick={() => store.toggleConnected(provider.key)} className="gap-1.5">
                {connected ? <><Check className="size-4" /> Connected</> : <><Plus className="size-4" /> Connect</>}
              </Button>
              {reviewCount > 0 && (
                <span className="inline-flex items-center gap-1.5 text-xs text-warning"><AlertTriangle className="size-3.5" />{reviewCount} field{reviewCount === 1 ? '' : 's'} need a look</span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Layers className="size-5 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <CardTitle className="text-sm">{provider.entity}</CardTitle>
              <p className="mt-0.5 text-xs text-muted-foreground">{provider.fields.length} fields</p>
            </div>
            <Badge className={cn('shrink-0', CONF_TONE[confidenceColor(provider.overallConfidence)])}>{confidenceLabel(provider.overallConfidence)}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {provider.fields.map((f, i) => {
              const shown = i < n;
              const isApproved = autoplay ? allApproved : approvedList.includes(f.apiName);
              const note = reviewNote(f);
              return (
                <li key={f.apiName} className={cn('flex items-start gap-3 rounded-md border px-3 py-2.5 transition-all duration-300',
                  shown ? 'opacity-100' : 'opacity-0',
                  note && !isApproved ? (note.kind === 'unmapped' ? 'border-dashed border-warning/50 bg-warning/5' : 'border-warning/50 bg-warning/5') : 'border-border bg-card')}>
                  <TypeBadge type={f.fieldType} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground">{f.displayName}</span>
                      <span className="font-mono text-xs text-muted-foreground">{f.apiName}</span>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">{f.classification}</p>
                    {note && !isApproved && (
                      <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-warning">
                        {note.kind === 'sensitive' ? <ShieldAlert className="size-3.5" /> : <AlertTriangle className="size-3.5" />} {note.text}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <ConfBadge value={f.confidence} />
                    {isApproved ? <Check className="size-4 text-success" />
                      : autoplay ? <Pencil className="size-4 text-muted-foreground/50" />
                      : <button type="button" aria-label={`Approve ${f.displayName}`} onClick={() => store.approveField(provider.key, f.apiName)}
                          className="rounded-md p-0.5 text-muted-foreground hover:bg-success/10 hover:text-success"><Check className="size-4" /></button>}
                  </div>
                </li>
              );
            })}
          </ul>
          <p className="mt-3 font-mono text-xs text-muted-foreground">
            {playing
              ? (provider.noSpec ? 'Probing live responses — sampling values…' : `Reading ${provider.protocol} schema…`)
              : (provider.noSpec
                  ? `✓ ${provider.fields.length} fields inferred from live responses — no spec, no docs`
                  : `✓ ${provider.fields.length} fields classified — no field mapping needed`)}
          </p>
        </CardContent>
      </Card>
    </>
  );
}

/* ── Field mappings (golden customer) ───────────────────────────────────── */

interface Contrib { source: Source; apiName: string; confidence: number; transform: string; updatedAt: string; ageHours: number; fill: number; manual: boolean }
interface MapRow { concept: string; goldenField: string; isNew: boolean; contribs: Contrib[]; conflict: boolean; winners: Set<number>; win: Contrib; reason: string }

function argBest<T>(items: T[], better: (a: T, b: T) => boolean): number {
  let best = 0; items.forEach((it, i) => { if (i > 0 && better(it, items[best]!)) best = i; }); return best;
}

function buildRows(sources: Source[], manualMap: Record<string, string>, authority: Record<string, string>): { rows: MapRow[]; unmapped: { source: Source; field: DemoField }[] } {
  const groups = new Map<string, Contrib[]>();
  const unmapped: { source: Source; field: DemoField }[] = [];
  for (const s of sources) {
    for (const f of s.fields) {
      const eff = f.concept || manualMap[mapKey(s.key, f.apiName)] || '';
      if (!eff) { unmapped.push({ source: s, field: f }); continue; }
      const c: Contrib = { source: s, apiName: f.apiName, confidence: f.confidence, transform: f.transform ?? 'identity', updatedAt: f.updatedAt ?? '—', ageHours: f.ageHours ?? 1e9, fill: f.fill ?? 1, manual: f.concept === '' };
      if (!groups.has(eff)) groups.set(eff, []);
      groups.get(eff)!.push(c);
    }
  }
  const keys = [...groups.keys()];
  const ordered = [...CONCEPT_ORDER.filter((c) => groups.has(c)), ...keys.filter((c) => !CONCEPT_ORDER.includes(c))];
  const rows = ordered.map((concept): MapRow => {
    const contribs = groups.get(concept)!;
    const golden = GOLDEN_CUSTOMER[concept];
    const isNew = concept.startsWith('new:');
    const goldenField = golden ? golden.field : isNew ? concept.slice(4) : concept;
    const conflict = contribs.length > 1;
    const rule = authority[concept] ?? 'Freshest';
    let winners: Set<number>;
    if (!conflict) winners = new Set([0]);
    else if (rule === 'Keep both') winners = new Set(contribs.map((_, i) => i));
    else if (rule === 'Most complete') winners = new Set([argBest(contribs, (a, b) => a.fill > b.fill)]);
    else if (rule === 'Highest confidence') winners = new Set([argBest(contribs, (a, b) => a.confidence > b.confidence)]);
    else winners = new Set([argBest(contribs, (a, b) => a.ageHours < b.ageHours)]);
    const win = contribs[[...winners][0] ?? 0]!;
    const reason = rule === 'Keep both' ? 'kept from every system'
      : rule === 'Most complete' ? `${Math.round(win.fill * 100)}% complete — most records filled`
      : rule === 'Highest confidence' ? `classified at ${confidenceLabel(win.confidence)}`
      : `changed ${win.updatedAt} — freshest copy`;
    return { concept, goldenField, isNew, contribs, conflict, winners, win, reason };
  });
  return { rows, unmapped };
}

/** A row for an unrecognised field — pick a target field or add a new one. */
function UnmappedRow({ source, field, onMap }: { source: Source; field: DemoField; onMap: (concept: string) => void }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  return (
    <div className="flex items-center gap-3 rounded-md border border-dashed border-warning/50 bg-warning/5 px-3 py-2.5">
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <span className="w-16 shrink-0 truncate rounded bg-muted px-1 text-3xs uppercase tracking-wide text-muted-foreground">{source.name}</span>
        <span className="truncate font-mono text-sm text-foreground">{field.apiName}</span>
        <span className="shrink-0 text-3xs text-warning">unrecognised</span>
      </div>
      <ArrowRight className="size-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        {adding ? (
          <div className="flex items-center gap-1">
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && slug(name)) onMap(`new:${slug(name)}`); if (e.key === 'Escape') setAdding(false); }}
              placeholder="new field name" className="min-w-0 flex-1 rounded-md border border-primary bg-card px-2 py-1 font-mono text-2xs text-foreground focus:outline-none" />
            <button type="button" onClick={() => slug(name) && onMap(`new:${slug(name)}`)} className="rounded-md bg-primary px-2 py-1 text-2xs font-medium text-primary-foreground disabled:opacity-50" disabled={!slug(name)}>Add</button>
            <button type="button" aria-label="Cancel" onClick={() => setAdding(false)} className="rounded-md p-1 text-muted-foreground hover:text-foreground"><X className="size-3.5" /></button>
          </div>
        ) : (
          <Select value="" onValueChange={(v) => { if (v === '__new') setAdding(true); else if (v) onMap(v); }}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Map to a field…" /></SelectTrigger>
            <SelectContent>
              {CONCEPT_ORDER.map((c) => <SelectItem key={c} value={c} className="text-xs">{GOLDEN_CUSTOMER[c]!.label} ({GOLDEN_CUSTOMER[c]!.field})</SelectItem>)}
              <SelectItem value="__new" className="text-xs">＋ Add a new field…</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>
      <span className="w-10 shrink-0 text-right"><ConfBadge value={field.confidence} /></span>
    </div>
  );
}

function MappingBody({ autoplay, store }: { autoplay: boolean; store: Store }) {
  const sources = SOURCES.filter((s) => store.connected.includes(s.key));
  const { rows, unmapped } = buildRows(sources, store.manualMap, store.authority);
  const resetKey = `${store.connected.join(',')}|${Object.keys(store.manualMap).length}`;
  const { n, play } = useReveal(rows.length, autoplay, resetKey);
  const allAccepted = autoplay ? n >= rows.length : store.accepted;
  const conflictCount = rows.filter((r) => r.conflict).length;

  return (
    <>
      {!autoplay && (
        <Card>
          <CardContent className="space-y-3 py-4">
            <p className="text-xs leading-relaxed text-muted-foreground">
              The same customer lives in every system under different field names. Inferrex maps each to a shared <span className="font-medium text-foreground">concept</span> and merges them into one record. Where two systems disagree it picks the source of truth from real lineage — <span className="font-medium text-foreground">when it last changed</span> and <span className="font-medium text-foreground">how complete it is</span>. Anything it can't recognise, you map by hand below.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">Systems</span>
              {SOURCES.map((s) => {
                const sel = store.connected.includes(s.key);
                return (
                  <button key={s.key} type="button" onClick={() => store.toggleConnected(s.key)}
                    className={cn('inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                      sel ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:text-foreground')}>
                    {sel && <Check className="size-3 text-primary" />}{s.name}<ProtocolBadge protocol={s.protocol} />
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-border pt-2">
              <p className="text-xs text-muted-foreground">
                {conflictCount > 0 ? <><span className="text-warning">{conflictCount} field{conflictCount === 1 ? '' : 's'} contested</span> — choose the source of truth</> : 'Connect another system to create conflicts'}
              </p>
              <Button size="sm" variant="outline" onClick={play} className="gap-1.5"><Play className="size-3.5" /> Re-map</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><ArrowRightLeft className="size-5" /></span>
              <div>
                <CardTitle className="text-base">{sources.length} system{sources.length === 1 ? '' : 's'} → Golden customer</CardTitle>
                <CardDescription className="mt-0.5 text-xs">{sources.map((s) => s.name).join(' + ')} reconciled by canonical concept</CardDescription>
              </div>
            </div>
            {!autoplay && <Button size="sm" onClick={() => store.setAccepted(true)} disabled={allAccepted} className="gap-1.5"><CheckCircle2 className="size-4" />{allAccepted ? 'Accepted' : 'Accept all'}</Button>}
          </div>
        </CardHeader>
      </Card>

      <div className="flex items-center gap-3 px-3 text-2xs font-medium uppercase tracking-wide text-muted-foreground">
        <span className="flex-1">Source fields</span>
        <span className="w-4 shrink-0" />
        <span className="flex-1">Golden customer</span>
        <span className="hidden w-20 shrink-0 sm:block">Transform</span>
        <span className="hidden w-32 shrink-0 sm:block">Source of truth</span>
        <span className="w-10 shrink-0 text-right">Conf.</span>
      </div>

      <Card>
        <CardContent className="space-y-2 py-4">
          {rows.map((r, i) => {
            const shown = i < n;
            const rule = store.authority[r.concept] ?? 'Freshest';
            return (
              <div key={r.concept} className={cn('flex items-start gap-3 rounded-md border px-3 py-2.5 transition-all duration-300',
                shown ? 'opacity-100' : 'opacity-0',
                r.conflict && !allAccepted ? 'border-warning/40 bg-warning/5' : allAccepted ? 'border-success/40 bg-success/5' : 'border-border bg-card')}>
                <div className="min-w-0 flex-1 space-y-1">
                  {r.contribs.map((c, ci) => {
                    const lost = r.conflict && !r.winners.has(ci);
                    return (
                      <div key={mapKey(c.source.key, c.apiName)} className="flex items-center gap-1.5">
                        <span className="w-16 shrink-0 truncate rounded bg-muted px-1 text-3xs uppercase tracking-wide text-muted-foreground">{c.source.name}</span>
                        <span className={cn('truncate font-mono text-sm', lost ? 'text-muted-foreground line-through' : 'text-foreground')}>{c.apiName}</span>
                        {c.manual && <span className="shrink-0 rounded bg-primary/10 px-1 text-3xs uppercase tracking-wide text-primary">mapped</span>}
                        {r.conflict && <span className={cn('shrink-0 whitespace-nowrap text-3xs', lost ? 'text-muted-foreground/50' : 'text-muted-foreground')}>{c.updatedAt} · {Math.round(c.fill * 100)}%</span>}
                      </div>
                    );
                  })}
                </div>
                <ArrowRight className="mt-1 size-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="truncate font-mono text-sm text-foreground">{r.goldenField}</span>
                    {r.isNew && <span className="shrink-0 rounded bg-primary/10 px-1 text-3xs uppercase tracking-wide text-primary">new</span>}
                  </span>
                  {r.conflict && <span className="mt-0.5 inline-flex items-center gap-1 text-3xs leading-tight text-primary"><Sparkles className="size-3 shrink-0" /> {r.win.source.name} · {r.reason}</span>}
                </div>
                <span className="mt-0.5 hidden w-20 shrink-0 sm:block"><Badge variant="outline" className="font-mono text-3xs">{r.win.transform}</Badge></span>
                <span className="hidden w-32 shrink-0 sm:block">
                  {r.conflict
                    ? (autoplay
                        ? <Badge variant="outline" className="text-xs">{rule}</Badge>
                        : <Select value={rule} onValueChange={(v) => store.setAuthorityRule(r.concept, v)}>
                            <SelectTrigger className="h-8 w-full text-xs" aria-label="Source of truth rule"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {AUTHORITY_RULES.map((a) => <SelectItem key={a} value={a} className="text-xs">{a}</SelectItem>)}
                            </SelectContent>
                          </Select>)
                    : <span className="text-xs text-muted-foreground/50">single source</span>}
                </span>
                <span className="mt-0.5 w-10 shrink-0 text-right"><ConfBadge value={r.win.confidence} /></span>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {unmapped.length > 0 && !autoplay && (
        <Card className="ring-1 ring-warning/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm text-warning"><AlertTriangle className="size-4" /> Needs your input — {unmapped.length} unrecognised field{unmapped.length === 1 ? '' : 's'}</CardTitle>
            <CardDescription className="text-xs">Inferrex couldn't classify these from their values. Map each one to a target field, or add a new field to the record.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {unmapped.map((u) => (
              <UnmappedRow key={mapKey(u.source.key, u.field.apiName)} source={u.source} field={u.field}
                onMap={(concept) => store.setManual(u.source.key, u.field.apiName, concept)} />
            ))}
          </CardContent>
        </Card>
      )}

      <p className="px-3 text-xs text-muted-foreground">
        One canonical vocabulary lets every system map to the same record — even fields you map by hand. Where two systems describe one field, the source of truth is decided from real lineage. One governed customer, with full provenance.
      </p>
    </>
  );
}

/* ── Self-healing ───────────────────────────────────────────────────────── */

const HEAL_STEPS = ['Stable', 'Change detected', 'Re-inferred', 'Remapped'] as const;

function HealBody({ autoplay }: { autoplay: boolean }) {
  const [idx, setIdx] = useState(0);
  const provider = SOURCES[idx]!;
  const reduced = prefersReducedMotion();
  const [step, setStep] = useState(autoplay && !reduced ? 0 : reduced ? HEAL_STEPS.length : 0);
  const [running, setRunning] = useState(autoplay && !reduced);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { setStep(reduced ? HEAL_STEPS.length : 0); setRunning(autoplay && !reduced); }, [idx, autoplay, reduced]);
  useEffect(() => {
    if (!running) return;
    if (step >= HEAL_STEPS.length) {
      if (!autoplay) { setRunning(false); return; }
      timer.current = setTimeout(() => setStep(0), 1900);
      return () => { if (timer.current) clearTimeout(timer.current); };
    }
    timer.current = setTimeout(() => setStep((s) => s + 1), 950);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [running, step, autoplay]);

  const reached = (k: number) => step >= k;
  const c = provider.change;

  return (
    <>
      {!autoplay && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SourceTabs activeKey={provider.key} onPick={(k) => setIdx(SOURCES.findIndex((s) => s.key === k))} connected={[]} />
          <Button size="sm" variant="outline" onClick={() => { setStep(0); setRunning(true); }} className="gap-1.5"><RadioTower className="size-3.5" /> Rename a field</Button>
        </div>
      )}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><Globe className="size-5" /></span>
              <div>
                <CardTitle className="text-base">{provider.displayName}</CardTitle>
                <CardDescription className="mt-0.5 text-xs">{provider.entity} · monitored for change</CardDescription>
              </div>
            </div>
            <Badge className={cn('shrink-0', reached(1) && step < HEAL_STEPS.length ? CONF_TONE.medium : CONF_TONE.high)}>
              {step >= HEAL_STEPS.length ? 'Healed' : reached(1) ? 'Healing…' : 'Healthy'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <ol className="flex flex-wrap gap-2">
            {HEAL_STEPS.map((s, i) => (
              <li key={s} className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors', reached(i + 1) ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground')}>
                {reached(i + 1) && <Check className="size-3" />}{s}
              </li>
            ))}
          </ol>
          <div className={cn('flex items-start gap-3 rounded-md border px-3 py-2.5 transition-colors', reached(1) && step < HEAL_STEPS.length ? 'border-warning/50 bg-warning/5' : 'border-border bg-card')}>
            <TypeBadge type={c.fieldType} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">{c.displayName}</span>
                <span className="inline-flex items-center gap-2 font-mono text-xs">
                  <span className={cn(reached(1) ? 'text-destructive line-through' : 'text-muted-foreground')}>{c.fromApi}</span>
                  {reached(2) && <><ArrowRight className="size-3 text-muted-foreground" /><span className="text-success">{c.toApi}</span></>}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">{reached(3) ? <>Still <span className="text-primary">{c.classification}</span> — mapping repaired</> : c.classification}</p>
            </div>
            <ConfBadge value={0.97} />
          </div>
          <p className="text-sm text-muted-foreground">
            {step >= HEAL_STEPS.length ? 'Mapping repaired automatically. Nobody was paged; nothing broke.'
              : reached(1) ? 'The provider renamed a field. Inferrex sees a rename, not a breakage…' : 'Mapping healthy, data flowing.'}
          </p>
        </CardContent>
      </Card>
    </>
  );
}

/* ── Coverage (stack) ───────────────────────────────────────────────────── */

function StackBody({ autoplay }: { autoplay: boolean }) {
  const reduced = prefersReducedMotion();
  const total = STACK_TOOLS.length;
  const [n, setN] = useState(reduced ? total : 0);
  const [scanning, setScanning] = useState(!reduced);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!scanning) return;
    if (n >= total) {
      if (!autoplay) { setScanning(false); return; }
      timer.current = setTimeout(() => setN(0), 1900);
      return () => { if (timer.current) clearTimeout(timer.current); };
    }
    timer.current = setTimeout(() => setN((x) => x + 1), 600);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [scanning, n, autoplay, total]);

  const [src, setSrc] = useState(INFER_SAMPLE);
  const [result, setResult] = useState<{ fields: InferredField[]; error?: string } | null>(null);
  const synced = Math.min(n, total);

  return (
    <>
      {!autoplay && (
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">{synced >= total ? `✓ All ${total} matched against the corpus` : `Syncing your stack… ${synced}/${total}`}</p>
          <Button size="sm" variant="outline" onClick={() => { setN(0); setScanning(true); }} className="gap-1.5"><RefreshCcw className="size-3.5" /> Re-scan</Button>
        </div>
      )}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Your stack, synced against the corpus</CardTitle>
          <CardDescription className="text-xs">Each system is read live — schema discovered, fields classified — no spec needed.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {STACK_TOOLS.map((t, i) => {
              const done = i < n;
              const syncing = scanning && i === n && n < total;
              return (
                <div key={t.name} className={cn('rounded-md border px-3 py-2 transition-colors',
                  done ? 'border-success/40 bg-success/5' : syncing ? 'border-primary/40 bg-primary/5' : 'border-border opacity-60')}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                      {done ? <Check className="size-4 text-success" /> : syncing ? <Loader2 className="size-4 animate-spin text-primary" /> : <span className="size-4 rounded-full border border-border" />}
                      {t.name}
                    </span>
                    <ProtocolBadge protocol={t.protocol} />
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5 pl-6">
                    {done ? (
                      <><span className="font-mono text-xs text-muted-foreground">{t.entity}</span>{t.fields.map((f) => <TypeBadge key={f.apiName} type={f.fieldType} />)}</>
                    ) : syncing ? (
                      <span className="font-mono text-xs text-muted-foreground">reading {t.protocol} schema…</span>
                    ) : (
                      <span className="font-mono text-xs text-muted-foreground/50">queued</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {!autoplay && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Infer a schema yourself</CardTitle>
            <CardDescription className="text-xs">Paste a sample record or schema — Inferrex reads it and classifies every field by type.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <textarea value={src} onChange={(e) => setSrc(e.target.value)} spellCheck={false} rows={6} aria-label="Schema to infer"
              className="w-full rounded-md border border-border bg-muted/20 p-3 font-mono text-xs text-foreground focus:border-primary focus:outline-none" />
            <div className="flex items-center gap-3">
              <Button size="sm" onClick={() => setResult(inferSchema(src))} className="gap-1.5"><ScanSearch className="size-4" /> Infer types</Button>
              {result && !result.error && <span className="text-xs text-muted-foreground">{result.fields.length} fields classified — no spec, no mapping</span>}
            </div>
            {result?.error && <p className="inline-flex items-center gap-1.5 text-xs text-warning"><AlertTriangle className="size-3.5" /> {result.error}</p>}
            {result && !result.error && result.fields.length > 0 && (
              <ul className="space-y-1.5">
                {result.fields.map((f) => (
                  <li key={f.key} className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2">
                    <TypeBadge type={f.fieldType} />
                    <span className="min-w-0 flex-1 truncate font-mono text-sm text-foreground">{f.key}</span>
                    <span className="hidden text-xs text-muted-foreground sm:block">{f.classification}</span>
                    <ConfBadge value={f.confidence} />
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
}

/* ── dispatcher ─────────────────────────────────────────────────────────── */

function renderBody(view: View, autoplay: boolean, store: Store) {
  switch (view) {
    case 'self-heal': return <HealBody autoplay={autoplay} />;
    case 'golden-record': return <MappingBody autoplay={autoplay} store={store} />;
    case 'stack': return <StackBody autoplay={autoplay} />;
    case 'infer':
    default: return <ConnectionBody autoplay={autoplay} store={store} />;
  }
}

function InteractiveApp({ initial }: { initial: View }) {
  const [view, setView] = useState<View>(initial);
  const store = useDemoStore();
  const m = VIEW_META[view];
  return <DemoWindow url={m.url} title={m.title} active={m.rail} onNavigate={setView}>{renderBody(view, false, store)}</DemoWindow>;
}

function AutoplayWindow({ scenario }: { scenario: View }) {
  const store = useDemoStore();
  const m = VIEW_META[scenario];
  return <DemoWindow url={m.url} title={m.title} active={m.rail}>{renderBody(scenario, true, store)}</DemoWindow>;
}

export function FlowDemo({ scenario, autoplay = false }: FlowDemoProps) {
  return autoplay ? <AutoplayWindow scenario={scenario} /> : <InteractiveApp initial={scenario} />;
}

/** Map a marketing demoId (from @ix:demo) to a scenario. */
export const DEMO_SCENARIO_BY_ID: Record<string, FlowScenario> = {
  'try-it-now': 'infer',
  'golden-record': 'golden-record',
  'self-healing': 'self-heal',
  'your-stack': 'stack',
};
